import { FunnelMapCanvas } from '../../../funnel-map-canvas/FunnelMapCanvas';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFunnelStagesMetadata,
  selectIsLoadingCursor,
  selectPlacingTemplate,
  selectTransientFunnel,
} from '../../../../reducers/multiFunnelFlowNoHistoryReducer';

import { useCallback, useMemo } from 'react';
import { selectAllVisibilityMaps } from '../../../../reducers/canvasLayersReducer';
import { VisibilityLayers } from '../../../../types/enums/VisibilityLayers';
import { useCalculatePills } from '../../../canvas-pills/useCalculatePills';
import { useConfigurationCanvasContext } from '../../configuration-canvas/ConfigurationCanvasContext';
import { visibilityMapToLayersSelectorVisibilityMap } from '../../configuration-canvas/canvas-container/layers-selector/utils';
import { useConfigurationCanvasState } from '../../configuration-canvas/useConfigurationCanvasState';
import { useConnectSteps } from '../../funnel-map-flow/dialogs/connect-steps-dialog/useConnectStep';
import { ConnectStep } from '../../funnel-map-flow/dialogs/connect-steps-dialog/ConnectSteps';
import {
  removeStepFunnelConnection,
  removeThirdPartyFunnelStepFunnelConnection,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import {
  CanvasPillTypes,
  GroupLabelEntityType,
  GroupPillEntityType,
  NodeEntityTypes,
} from '../../../sweep-canvas/canvas-types';
import { useFitAroundElements } from '../../configuration-canvas-filters/useFitAroundElements';
import { useGetReservedSpaceForPanels } from '../../configuration-canvas/panels-reserve-space/ReserveSpaceForPanelsCtx';
import { usePlacePluginIntoFunnel } from '../../funnel-map-flow/templates/funnel-map-plugins/usePlacePluginIntoFunnel';
import { PlacingPluginLayerConsumer } from '../../funnel-map-flow/templates/placing-plugin/PlacingPluginLayer';
import useObjectTypesWithFetch from '../../../../hooks/useObjectTypesWithFetch';
import {
  FunnelMapCanvasProps,
  OnRemoveFunnelLink,
  OnThirdPartyLabelClick,
  OnThirdPartyStepClick,
} from '../../../funnel-map-canvas/types';
import { FunnelType } from '../../../../types/enums/FunnelType';
import {
  setEditingThirdPartyFunnelId,
  setEditThirdPartyStep,
} from '../../../third-party-funnels/thirdPartyFunnelsReducer';
import { UnitedCanvasThirdPartyFunnelComponents } from '../../../third-party-funnels/UnitedCanvasThirdPartyFunnel';
import { ZeroStateRightPanel } from './zero-state-right-panel/ZeroStateRightPanel';
import useStagesWithSparkleIcon from './useStagesWithSparkleIcon';
import { useFeatureToggle } from '../../../common/useFeatureToggle';
import { useUnitedSweepCanvasReorderCtx } from './UnitedSweepCanvasReorderCtx';

interface UnitedConnectedFunnelMapFlowCanvasProps {
  onSweepElementsChange: FunnelMapCanvasProps['onSweepElementsChange'];
  moveGroups: FunnelMapCanvasProps['moveGroups'];
}

export const UnitedConnectedFunnelMapFlowCanvas = ({
  moveGroups,
  onSweepElementsChange,
}: UnitedConnectedFunnelMapFlowCanvasProps) => {
  const dispatch = useDispatch();
  const { canvasReorderSteps: canvasReorderStepsFT } = useFeatureToggle();
  const placingTemplate = useSelector(selectPlacingTemplate);
  const transientFunnel = useSelector(selectTransientFunnel);
  const isLoadingCursor = useSelector(selectIsLoadingCursor);
  const { pills, canvasFunnelMap: funnelMap } = useConfigurationCanvasState();
  const { canvasCrmOrgId: crmOrgId } = useConfigurationCanvasContext();
  useCalculatePills({ crmOrgId, funnelMap });
  const { rightReservedSpace } = useGetReservedSpaceForPanels();
  const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId });

  const {
    configurationCanvasProps: {
      visibilityElements,
      onFunnelStepClick,
      onFunnelLabelClick,
      onFunnelLabelPillClick,
      onStageGateClick,
      selectedStageId,
      selectedGateId,
      selectedFunnelId,
    },
  } = useConfigurationCanvasContext();

  const allVisibilityMaps = useSelector(selectAllVisibilityMaps);
  const visibilityMap = useMemo(() => {
    return visibilityMapToLayersSelectorVisibilityMap(
      visibilityElements.type === 'map'
        ? {
            // Remove old layers from the canvas if present
            [VisibilityLayers.NURTURING_STEPS]: true,
            [VisibilityLayers.GATES]: false,
            ...visibilityElements.visibilityMap,
          }
        : allVisibilityMaps?.[visibilityElements.layersId] || {},
    );
  }, [allVisibilityMaps, visibilityElements]);

  const { placePlugin, onPluginClick, isPlacingPlugin } = usePlacePluginIntoFunnel({
    crmOrgId,
  });

  const { connectStepsProps, handleConnectStepsButtonClick, holdNodeHighlighted } =
    useConnectSteps(funnelMap);

  const onRemoveFunnelLinkClick: OnRemoveFunnelLink = useCallback(
    ({
      linkId,
      originFunnelType,
      sourceFunnelId,
      targetFunnelId,
      sourceStageId,
      targetStageId,
    }) => {
      if (originFunnelType === FunnelType.SALESFORCE) {
        dispatch(
          removeStepFunnelConnection({
            sourceFunnelId,
            sourceStageId,
            targetFunnelId,
            targetStageId,
          }),
        );
      }
      if (originFunnelType === FunnelType.THIRD_PARTY) {
        dispatch(
          removeThirdPartyFunnelStepFunnelConnection({
            sourceFunnelId,
            linkId,
            sourceStageId,
          }),
        );
      }
    },
    [dispatch],
  );
  const funnelStageMetadata = useSelector(selectFunnelStagesMetadata);

  const { fitOnElements } = useFitAroundElements({
    funnelsData: funnelMap.funnelsData,
    recordTypesData: funnelMap.recordTypesData,
  });

  const _onFunnelLabelClick = useCallback(
    (props: {
      funnelId: string;
      entity: GroupPillEntityType | GroupLabelEntityType;
      event: React.MouseEvent | React.TouchEvent;
    }) => {
      if (
        props.entity.type === NodeEntityTypes.GroupPill &&
        props.entity.canvasPillType === CanvasPillTypes.groupAggregated
      ) {
        fitOnElements([props.funnelId], true);
        return;
      }

      if (onFunnelLabelClick) {
        onFunnelLabelClick(props);
      }
    },
    [fitOnElements, onFunnelLabelClick],
  );

  const onThirdPartyLabelClick: OnThirdPartyLabelClick = useCallback(
    ({ funnelId }: { funnelId: string }) => {
      dispatch(setEditingThirdPartyFunnelId(funnelId));
    },
    [dispatch],
  );

  const onThirdPartyStepClick: OnThirdPartyStepClick = useCallback(
    ({ stepId, funnelId }: { stepId: string; funnelId: string }) => {
      dispatch(setEditThirdPartyStep({ funnelId, stepId }));
    },
    [dispatch],
  );
  const stagesWithSparkleIcon = useStagesWithSparkleIcon(funnelMap);
  const { reorderStepsMode, setReorderStepsMode } = useUnitedSweepCanvasReorderCtx();

  return (
    <>
      <PlacingPluginLayerConsumer />
      <ConnectStep {...connectStepsProps} />
      <UnitedCanvasThirdPartyFunnelComponents funnelMap={funnelMap} />
      <ZeroStateRightPanel />
      <FunnelMapCanvas
        funnelMap={funnelMap}
        visibilityMap={visibilityMap}
        onStageGateClick={onStageGateClick}
        onFunnelStepClick={onFunnelStepClick}
        onFunnelLabelClick={onFunnelLabelClick ? _onFunnelLabelClick : undefined}
        onFunnelLabelPillClick={onFunnelLabelPillClick ? onFunnelLabelPillClick : undefined}
        pills={pills}
        isLoadingCursor={isLoadingCursor}
        moveGroups={moveGroups}
        autoFitViewOnFirstNodes
        holdNodeHighlighted={holdNodeHighlighted}
        onRemoveFunnelLinkClick={onRemoveFunnelLinkClick}
        onConnectFunnelOrStepsClick={handleConnectStepsButtonClick}
        onSweepElementsChange={onSweepElementsChange}
        selectedStageId={selectedStageId}
        selectedGateId={selectedGateId}
        selectedFunnelId={selectedFunnelId}
        placingTemplate={placingTemplate}
        funnelStageMetadata={funnelStageMetadata}
        transientFunnel={transientFunnel}
        onPluginClick={onPluginClick}
        onFunnelOverlayClick={isPlacingPlugin ? placePlugin : undefined}
        controlsRightMargin={rightReservedSpace}
        objectTypesByName={objectTypesByName}
        onThirdPartyLabelClick={onThirdPartyLabelClick}
        onThirdPartyStepClick={onThirdPartyStepClick}
        stagesWithSparkleIcon={stagesWithSparkleIcon}
        enableStepsReordering={canvasReorderStepsFT}
        reorderStepsMode={reorderStepsMode}
        onDeactivateReorderStepsMode={useCallback(
          () => setReorderStepsMode(undefined),
          [setReorderStepsMode],
        )}
      />
    </>
  );
};
